
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Descriptions, DescriptionsProps } from "antd";

import { Product } from "@/types/product";
import SText from "@/styles/SText";

interface ProductDescProps {
  product: Product | null;
  otherItems?: DescriptionsProps["items"];
  showtype?: 'MAIN' | 'SUPPLIER' | 'OTHER';
  merged?: boolean;
}

const ProductDesc: FC<ProductDescProps> = ({ product, otherItems, showtype = "MAIN", merged = false }) => {
  const { t } = useTranslation();

  const items: DescriptionsProps["items"] = useMemo(() => {
    if (showtype === 'MAIN') return [
      {
        key: "product",
        label: t("product.code"),
        children: product?.code || "",
      },
      {
        key: "name",
        label: t("product.name"),
        children: product?.name || "",
      },
      {
        key: "uom",
        label: t("product.uom"),
        children: product?.uom?.toLocaleUpperCase() || "",
      },
      {
        key: "cbm",
        label: t("product.cbm"),
        children: product?.cbm || "",
      },
      {
        key: "brand1",
        label: t("product.brand1"),
        children: <SText text={product?.model?.toLocaleUpperCase() || ""} style={{ width: "100%" }} />,
      },
      {
        key: "brand2",
        label: t("product.brand2"),
        children: <SText text={product?.t1?.toLocaleUpperCase() || ""} style={{ width: "100%" }} />,
      },
      {
        key: "process",
        label: t("product.process"),
        children: <SText text={product?.process || ""} style={{ width: "100%" }} />,
      },
      {
        key: "hsCode",
        label: t("category.hsCode"),
        children: <SText text={product?.hsCode || ""} style={{ width: "100%" }} />,
      },
      {
        key: "desc",
        label: t("common.desc"),
        children: <SText text={product?.desc || ""} style={{ width: "100%" }} />,
      },
      ...(otherItems || [])
    ]
    else if (showtype === 'SUPPLIER') return [
      {
        key: "product",
        label: t("supplierQuotation.productCode"),
        children: product?.code || "",
      },
      {
        key: "name",
        label: t("supplierQuotation.productName"),
        children: product?.name || "",
      },
      {
        key: "uom",
        label: t("product.uom"),
        children: product?.uom?.toLocaleUpperCase() || "",
      },
      {
        key: "model",
        label: t("supplierQuotation.productModel"),
        children: product?.model?.toLocaleUpperCase() || "",
        span: merged ? undefined : 3
      },
      ...(otherItems || [])
    ]
    else return otherItems ? [...otherItems] : []
  }, [t, product, otherItems, showtype]);

  return product ? <Descriptions items={items} /> : null;
}

export default ProductDesc;